import React, { useEffect, useState } from 'react';
import styles from './DashboardModule.module.css';
import { Project, DashboardMetric } from '../../types';
import {
  LocationIcon,
  CalendarIcon,
  UsersIcon,
  ActivityIcon,
  ClockIcon
} from '../common/Icons/Icons';
import { Card, CardContent } from '../common/Card/Card';
import { dashboardAPI, projectsAPI, getApiBaseUrl } from '../../services/api';

interface DashboardModuleProps {
  project: Project;
  metrics?: DashboardMetric[];
}

export const DashboardModule: React.FC<DashboardModuleProps> = ({ project, metrics: propMetrics }) => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [projectStats, setProjectStats] = useState<any>(null);
  const [metrics, setMetrics] = useState<DashboardMetric[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, [project.id]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const API_BASE = getApiBaseUrl();
      const [dashboard, stats, sentimentRes] = await Promise.all([
        dashboardAPI.getProjectDashboard(project.id).catch(() => null),
        projectsAPI.getStats(project.id).catch(() => null),
        fetch(`${API_BASE}/projects/${project.id}/sentiment`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json()).catch(() => null)
      ]);

      setDashboardData(dashboard);
      setProjectStats(stats);

      const escalationLevel = sentimentRes?.sentimentAnalysis?.overallEscalationLevel;

      // Convert API data to DashboardMetric format - 3 key metrics
      const apiMetrics: DashboardMetric[] = [];

      // Stakeholders
      apiMetrics.push({
        icon: <UsersIcon />,
        iconBackground: '#E3F2FD',
        value: stats?.relationships?.total || 0,
        label: 'Stakeholders',
      });

      // Vragen (totaal)
      const totalQuestions = stats?.questions?.total || 0;
      apiMetrics.push({
        icon: <ActivityIcon />,
        iconBackground: '#FFF3E0',
        value: totalQuestions,
        label: 'Vragen',
      });

      // Voortgang (taken)
      const completionRate = Math.round(stats?.tasks?.completionRate || 0);
      apiMetrics.push({
        icon: <ClockIcon />,
        iconBackground: '#F3E5F5',
        value: stats?.tasks?.total > 0 ? `${completionRate}%` : '-',
        label: 'Voortgang',
      });

      setMetrics(apiMetrics);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Fall back to prop metrics if API fails
      if (propMetrics) {
        setMetrics(propMetrics);
      }
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}>Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className={styles.dashboardModule}>
      {/* Project Header */}
      <div
        className={(project as any).headerImage ? styles.projectHeaderWithImage : styles.projectHeader}
        style={{
          backgroundImage: (project as any).headerImage ? `url(${(project as any).headerImage})` : undefined
        }}
      >
        {/* Overlay to ensure text readability - only when image is present */}
        {(project as any).headerImage && (
          <div className={styles.projectHeaderOverlay} />
        )}
        <div className={styles.projectHeaderContent} style={{ position: 'relative', zIndex: 1 }}>
          <div>
            <h1 className={styles.projectTitle}>{project.name}</h1>
            {((project as any).shortDescription || project.description) && (
              <p className={styles.projectDescription}>
                {(project as any).shortDescription || project.description}
              </p>
            )}
            <div className={styles.projectMeta}>
              <span className={styles.projectMetaItem}>
                <LocationIcon />
                {project.location}
              </span>
              <span className={styles.projectMetaItem}>
                <CalendarIcon />
                {project.period || (project.startDate && project.endDate
                  ? `${new Date(project.startDate).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })} - ${new Date(project.endDate).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}`
                  : project.startDate
                    ? new Date(project.startDate).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })
                    : '')}
              </span>
            </div>
            <div className={styles.projectStatusBadge}>
              {project.status === 'planning' && 'Planning'}
              {project.status === 'active' && 'Actief'}
              {project.status === 'completed' && 'Voltooid'}
              {project.status === 'paused' && 'Gepauzeerd'}
              {project.status === 'cancelled' && 'Geannuleerd'}
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid — clickable tiles */}
      <div className={styles.metricsGrid}>
        {metrics.map((metric, index) => {
          const links = ['strategy', 'vragen', 'werkplanning'];
          const targetModule = links[index] || 'dashboard';
          return (
            <a
              key={index}
              href={`/project/${project.id}/${targetModule}`}
              className={styles.metricLink}
              onClick={(e) => { e.preventDefault(); window.location.href = `/project/${project.id}/${targetModule}`; }}
            >
              <Card className={styles.metricCard}>
                <CardContent>
                  <div className={styles.metricContent}>
                    <div
                      className={styles.metricIcon}
                      style={{ backgroundColor: metric.iconBackground }}
                    >
                      {metric.icon}
                    </div>
                    <div className={styles.metricInfo}>
                      <div className={styles.metricValue}>{metric.value}</div>
                      <div className={styles.metricLabel}>{metric.label}</div>
                      {metric.change && (
                        <div className={`${styles.metricChange} ${styles[metric.change.type]}`}>
                          {metric.change.value}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>
          );
        })}
      </div>

      {/* Project Info Section */}
      {project.description && (
        <div className={styles.projectInfoSection}>
          <div className={styles.projectInfoGrid}>
            <div className={styles.projectInfoMain}>
              <h2 className={styles.sectionTitle}>Over dit project</h2>
              <div className={styles.projectInfoText}>
                {project.description.split('\n').map((p: string, i: number) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
            <div className={styles.projectInfoDetails}>
              <h2 className={styles.sectionTitle}>Projectinformatie</h2>
              <div className={styles.projectInfoCard}>
                {(() => {
                  const locData = (project as any).locationData;
                  const city = locData?.city || project.location;
                  const address = locData?.address || '';
                  const province = locData?.province || '';
                  return (
                    <>
                      {city && (
                        <div
                          className={`${styles.projectInfoRow} ${styles.projectInfoRowClickable}`}
                          onClick={() => { window.location.href = `/project/${project.id}/gebiedsanalyse`; }}
                        >
                          <span className={styles.projectInfoLabel}>
                            <LocationIcon />
                            Locatie
                          </span>
                          <span className={styles.projectInfoValue}>
                            {address ? `${address}, ${city}` : city}
                            {province ? `, ${province}` : ''}
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}
                {project.startDate && (
                  <div className={styles.projectInfoRow}>
                    <span className={styles.projectInfoLabel}>
                      <CalendarIcon />
                      Periode
                    </span>
                    <span className={styles.projectInfoValue}>
                      {new Date(project.startDate).toLocaleDateString('nl-NL', { year: 'numeric', month: 'long' })}
                      {project.endDate && ` - ${new Date(project.endDate).toLocaleDateString('nl-NL', { year: 'numeric', month: 'long' })}`}
                    </span>
                  </div>
                )}
                {(project as any).company?.name && (
                  <div className={styles.projectInfoRow}>
                    <span className={styles.projectInfoLabel}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                      </svg>
                      Opdrachtgever
                    </span>
                    <div className={styles.companyBlock}>
                      {(project as any).company.logo && (
                        <img
                          src={(project as any).company.logo}
                          alt={(project as any).company.name}
                          className={styles.companyLogoLarge}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      <span className={styles.companyName}>{(project as any).company.name}</span>
                    </div>
                  </div>
                )}
                <div className={styles.projectInfoRow}>
                  <span className={styles.projectInfoLabel}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                    Status
                  </span>
                  <span className={styles.projectInfoValue}>
                    {project.status === 'planning' && 'Planning'}
                    {project.status === 'active' && 'Actief'}
                    {project.status === 'completed' && 'Voltooid'}
                    {project.status === 'paused' && 'Gepauzeerd'}
                    {project.status === 'cancelled' && 'Geannuleerd'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Charts Section */}
      {(dashboardData?.sentiment?.trend?.length > 0 || projectStats?.tasks?.total > 0) && (
        <div className={styles.chartsGrid}>
          {dashboardData?.sentiment?.trend?.length > 0 && (
            <Card>
              <CardContent>
                <h2 className={styles.sectionTitle}>Sentiment Trend</h2>
                <div className={styles.chartPlaceholder}>
                  <div className={styles.miniChart}>
                    {dashboardData.sentiment.trend?.slice(-7).map((point: any, index: number) => (
                      <div
                        key={index}
                        className={styles.chartBar}
                        style={{
                          height: `${Math.max(20, (point.average + 1) * 50)}%`,
                          backgroundColor: point.average > 0 ? '#4CAF50' : '#f44336'
                        }}
                        title={`${point.date}: ${point.average.toFixed(2)}`}
                      />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {projectStats?.tasks?.total > 0 && (
            <Card>
              <CardContent>
                <h2 className={styles.sectionTitle}>Takenstatus</h2>
                <div className={styles.statusList}>
                  <div className={styles.statusItem}>
                    <span className={styles.statusLabel}>Te doen</span>
                    <span className={styles.statusValue}>{projectStats.tasks.total - projectStats.tasks.completed - projectStats.tasks.inProgress}</span>
                  </div>
                  <div className={styles.statusItem}>
                    <span className={styles.statusLabel}>In uitvoering</span>
                    <span className={styles.statusValue}>{projectStats.tasks.inProgress}</span>
                  </div>
                  <div className={styles.statusItem}>
                    <span className={styles.statusLabel}>Afgerond</span>
                    <span className={styles.statusValue}>{projectStats.tasks.completed}</span>
                  </div>
                  {projectStats.tasks.overdue > 0 && (
                    <div className={styles.statusItem}>
                      <span className={styles.statusLabel} style={{ color: '#E6007E' }}>Te laat</span>
                      <span className={styles.statusValue} style={{ color: '#E6007E' }}>{projectStats.tasks.overdue}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};